import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import config from "../../config";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";

const omitNullish = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const out = {};
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (v !== null && v !== undefined) {
            out[k] = v;
        }
    });
    return out;
};

const fetchTableOrders = (tableId) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `orders_list`,
        args: "{}",
    };
    return axios.get(`method/frappe_helper.api.call`, { params });
};

export const useTableOrders = (
    tableId,
    extraDescriptor = "",
    onSuccess,
    onError
) => {
    return useQuery(
        ["getTableOrders" + extraDescriptor, tableId],
        async () => {
            const data = await fetchTableOrders(tableId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: !!tableId,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useTableOrdersLazy = (
    tableId,
    extraDescriptor = "",
    onSuccess,
    onError
) => {
    return useQuery(
        ["getTableOrders" + extraDescriptor, tableId],
        async () => {
            const data = await fetchTableOrders(tableId);
            return data.data.message;
        },
        {
            onSuccess,
            onError,
            enabled: false,
        }
    );
};

const createTableOrder = (tableId, formData) => {
    const params = {
        model: `Restaurant Object`,
        name: tableId,
        method: `add_order`,
        args: '{"client":"obj_69b9-9d-463-847-91b8f"}',
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useCreateTableOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, formData }) => {
            const data = await createTableOrder(tableId, formData);
            return data;
        },
        {
            onSuccess: (data, { tableId }) => {
                // Refresh the query for the table order items.
                queryClient.invalidateQueries(["getTableOrders", tableId]);
                queryClient.refetchQueries(["getTableOrders", tableId]);
            },
        }
    );
};

const addNewOrder = ({
    orderItems,
    table,
    customer,
    address,
    masterPassword,
    dinners,
    dinners_children,
    dinners_infants,
    notes,
    sendToKitchen = true,
}) => {
    const args = omitNullish({
        order_items: orderItems,
        send_to_kitchen: !!sendToKitchen,
        customer,
        address,
        dinners,
        dinners_children,
        dinners_infants,
        notes,
    });

    const params = {
        model: "Restaurant Object",
        name: table,
        method: "add_order",
        args: JSON.stringify(args),
    };

    if (masterPassword) {
        params.master_password = masterPassword;
    }

    return axios.post(
        masterPassword
            ? "method/restaurant_management.api.escalate_privilege"
            : "method/frappe_helper.api.call",
        params
    );
};

export const useAddNewOrder = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({
            orderItems,
            table,
            customer,
            address,
            masterPassword,
            dinners,
            dinners_children,
            dinners_infants,
            notes,
        }) => {
            const { data } = await addNewOrder({
                orderItems,
                table,
                customer,
                address,
                masterPassword,
                dinners,
                dinners_children,
                dinners_infants,
                notes,
                sendToKitchen: true,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

export const useAddNewOrderDraft = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({
            orderItems,
            table,
            customer,
            address,
            masterPassword,
            dinners,
            dinners_children,
            dinners_infants,
            notes,
        }) => {
            const { data } = await addNewOrder({
                orderItems,
                table,
                customer,
                address,
                masterPassword,
                dinners,
                dinners_children,
                dinners_infants,
                notes,
                sendToKitchen: false,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

const pushOrderItems = ({ orderItems, order, masterPassword, sendToKitchen = true }) => {
    const args = omitNullish({
        order_items: orderItems,
        send_to_kitchen: !!sendToKitchen,
    });

    const params = {
        model: "Table Order",
        name: order.name,
        method: "_push_items",
        args: JSON.stringify(args),
    };
    if (masterPassword) {
        params.master_password = masterPassword;
    }
    return axios.post(
        masterPassword
            ? "method/restaurant_management.api.escalate_privilege"
            : "method/frappe_helper.api.call",
        params
    );
};

const setOrderReadyTime = ({ order }) => {
    const params = {
        model: "Table Order",
        name: order.name,
        method: "set_order_ready_time",
        args: "{}",
    };
    return axios.post("method/frappe_helper.api.call", params);
};

export const useSetOrderReadyTime = () => {
    return useMutation(async ({ order }) => {
        const { data } = await setOrderReadyTime({ order });
        return data;
    });
};

export const usePushOrderItems = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderItems, order, masterPassword }) => {
            const { data } = await pushOrderItems({
                orderItems,
                order,
                masterPassword,
                sendToKitchen: true,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                // Show dialog and wait for master password
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                // Execute with master password
                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                // User cancelled the dialog
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            // Execute directly without master password
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

export const usePushOrderItemsDraft = () => {
    const { showMasterPasswordDialog } = useMasterPassword();

    const mutation = useMutation(
        async ({ orderItems, order, masterPassword }) => {
            const { data } = await pushOrderItems({
                orderItems,
                order,
                masterPassword,
                sendToKitchen: false,
            });
            return data;
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                // Show dialog and wait for master password
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                // Execute with master password
                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                // User cancelled the dialog
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

const sendTableOrder = ({ orderId }) => {
    const params = {
        model: "Table Order",
        name: orderId,
        method: "send",
        args: "{}",
    };
    return axios.post("method/frappe_helper.api.call", params);
};

export const useSendTableOrder = () => {
    return useMutation(async ({ orderId }) => {
        const { data } = await sendTableOrder({ orderId });
        return data;
    });
};

const deleteTableOrder = (tableId, orderName) => {
    const params = {
        model: `Table Order`,
        name: orderName,
        method: `_delete`,
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useDeleteTableOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ tableId, orderName }) => {
            const data = await deleteTableOrder(tableId, orderName);
            return data;
        },
        {
            onSuccess: (data, { tableId }) => {
                // Refresh the query for the table order items.
                queryClient.refetchQueries(["getTableOrders", tableId]);
            },
        }
    );
};

const reserveTableCall = (tableId, customerName) => {
    const params = {
        tableName: tableId,
        customerName: customerName,
    };
    return axios.post(`method/restaurant_management.api.reserve_table`, params);
};

export const useReserveTableData = () => {
    return useMutation(async ({ tableId, customerName }) => {
        const data = await reserveTableCall(tableId, customerName);
        return data;
    });
};

const assignCustomerToOrderItem = (orderId, payload) => {
    const params = {
        desk_form: `restaurant-order-customer`,
        doc_name: orderId,
        data: JSON.stringify(payload),
    };
    return axios.post(
        `method/frappe_helper.frappe_helper.doctype.desk_form.desk_form.accept`,
        params
    );
};

export const useAssignCustomerToTableData = (orderId, payload) => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, payload }) => {
            const data = await assignCustomerToOrderItem(orderId, payload);
            return data;
        },
        {
            onSuccess: (data, { orderId, payload }) => {
                // we will refresh things.
                const { table } = payload;
                if (table) {
                    queryClient.invalidateQueries(["getTableOrders", table]);
                    queryClient.refetchQueries(["getTableOrders", table]);
                }

                queryClient.invalidateQueries(
                    "getTableTakeAwayOrder" + config.takeaway_table_identity
                ); // Replace 'getTableOrders' with your query key
                queryClient.refetchQueries(
                    "getTableTakeAwayOrder" + config.takeaway_table_identity
                ); // Replace 'getTableOrders' with your query key
            },
        }
    );
};

const moveTableOrders = (orders, destTable) => {
    const params = {
        orders: JSON.stringify(orders),
        destinationTable: destTable,
    };
    return axios.post(
        `method/restaurant_management.api.move_table_orders`,
        params
    );
};

export const useMoveTableOrdersData = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orders, destTable, floorId }) => {
            const data = await moveTableOrders(orders, destTable);
            return data;
        },
        {
            onSuccess: (data, { floorId }) => {
                // we will refresh things.
                queryClient.invalidateQueries(["getFloorFullDetails", floorId]);
            },
        }
    );
};

const fetchInvoiceData = ({ orderId, entry_items, discount_percentage }) => {
    const args = {
        entry_items,
        // entry_items: JSON.stringify(entry_items),
        discount_percentage: discount_percentage,
    };
    console.log(orderId);
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `get_invoice`,
        args: JSON.stringify(args),
    };
    return axios.post(`method/frappe_helper.api.call`, params);
};

export const useGetInvoiceData = ({
    orderId,
    entry_items,
    discount_percentage = 0,
}) => {
    return useQuery(
        [`getInvoiceData_${orderId}`],
        async () => {
            const data = await fetchInvoiceData({
                orderId,
                entry_items,
                discount_percentage,
            });
            return data.data.message;
        },
        {
            enabled:
                !!orderId &&
                !!entry_items &&
                Object.keys(entry_items).length > 0,
            cacheTime: 0,
            refetchOnMount: true,
        }
    );
};

export const useFetchGetInvoiceData = () => {
    return useMutation(
        async ({ orderId, entry_items, discount_percentage }) => {
            const data = await axios.post(`method/frappe_helper.api.call`, {
                model: `Table Order`,
                name: orderId,
                method: `get_invoice`,
                args: JSON.stringify({
                    entry_items,
                    discount_percentage,
                }),
            });
            return data.data.message;

        },
        {
            onSuccess: (data) => {
                return data;
                // return data.data.message;
            },
        }
    );
};
